import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Flex,
  Input,
  Select,
  Typography,
} from 'antd';
import { memo, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebounceCallback } from '@/hooks/useDebounce';
import { Label, Meta } from '@/UI/typography';
import { defaultFilterShowSearch } from '@/utils/selectHelpers';

import { CollapsibleFilterFooter } from './CollapsibleFilterFooter';
import {
  FilteredListingSearchRow,
  type FilteredListingSearchRowProps,
} from './FilteredListingSearchRow';
import { useCollapsibleFilterSection } from './useCollapsibleFilterSection';

import type {
  FilterGroup,
  FilterSelectOption,
} from './filteredListingFilterGroups';
import type React from 'react';

const { RangePicker } = DatePicker;

const FILTER_VALUE_SEP = '::';

function SidebarSectionFrame({
  children,
  showDivider = true,
}: {
  children: React.ReactNode;
  showDivider?: boolean;
}) {
  return (
    <>
      <Flex vertical>{children}</Flex>
      {showDivider ? <Divider size="medium" /> : null}
    </>
  );
}

function SidebarSection({
  children,
  showDivider,
}: {
  children: React.ReactNode;
  showDivider?: boolean;
}) {
  return (
    <SidebarSectionFrame showDivider={showDivider}>
      {children}
    </SidebarSectionFrame>
  );
}

function SidebarTopToolbarSection({ children }: { children: React.ReactNode }) {
  return <SidebarSectionFrame>{children}</SidebarSectionFrame>;
}

function FilterGroupSection({
  title,
  showDivider,
  children,
}: {
  title?: ReactNode;
  showDivider?: boolean;
  children: ReactNode;
}) {
  return (
    <SidebarSection showDivider={showDivider}>
      {title != null && <Label>{title}</Label>}
      {children}
    </SidebarSection>
  );
}

function encodeFilterCheckboxValue(
  groupId: string,
  value: string | number,
): string {
  return `${groupId}${FILTER_VALUE_SEP}${value}`;
}

function decodeFilterCheckboxValue(encoded: string | number): string | number {
  const s = String(encoded);
  const sep = s.indexOf(FILTER_VALUE_SEP);
  if (sep < 0) return encoded;
  const raw = s.slice(sep + FILTER_VALUE_SEP.length);
  const asNum = Number(raw);
  return raw !== '' && !Number.isNaN(asNum) && String(asNum) === raw
    ? asNum
    : raw;
}

function FilterCheckboxOptionRow({
  label,
  count,
  labelSuffix,
  checkboxValue,
  checked,
  onCheckedChange,
}: {
  label: ReactNode;
  count?: number;
  labelSuffix?: ReactNode;
  /** When set, used as Checkbox.Group child value. */
  checkboxValue?: string | number;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <Flex align="center" justify="space-between" style={{ width: '100%' }}>
      <Checkbox
        value={checkboxValue}
        checked={checked}
        onChange={
          onCheckedChange ? (e) => onCheckedChange(e.target.checked) : undefined
        }
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--ant-font-size-sm)',
        }}
      >
        <Flex align="center" gap={4} wrap={false}>
          {label}
          {labelSuffix}
        </Flex>
      </Checkbox>
      {count != null && (
        <Meta
          style={{
            flexShrink: 0,
            minWidth: 24,
            textAlign: 'right',
          }}
        >
          {count}
        </Meta>
      )}
    </Flex>
  );
}

function CheckboxListGroupView({
  group,
  showDivider,
}: {
  group: Extract<FilterGroup, { kind: 'checkbox-list' }>;
  showDivider?: boolean;
}) {
  const cap = group.collapsibleCap ?? 5;
  const options = useMemo(() => {
    const seen = new Set<string>();
    return group.options.filter((opt) => {
      const value = String(opt.value);
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }, [group.options]);
  const collapse = useCollapsibleFilterSection(options.length, cap);

  return (
    <FilterGroupSection title={group.title} showDivider={showDivider}>
      {options.length === 0 && group.emptyContent != null ? (
        typeof group.emptyContent === 'string' ? (
          <Typography.Text type="secondary">
            {group.emptyContent}
          </Typography.Text>
        ) : (
          group.emptyContent
        )
      ) : (
        <>
          <Checkbox.Group
            name={group.id}
            data-testid={
              group.id === 'lists' ? 'rm-filter-group-lists' : undefined
            }
            onChange={(values) =>
              group.onChange(values.map(decodeFilterCheckboxValue))
            }
            value={group.value.map((v) =>
              encodeFilterCheckboxValue(group.id, v),
            )}
          >
            <Flex vertical gap={4} style={{ width: '100%' }}>
              {options.map((opt, idx) => {
                if (opt.hidden || collapse.isHidden(idx)) {
                  return null;
                }
                return (
                  <FilterCheckboxOptionRow
                    key={`${group.id}-${String(opt.value)}-${idx}`}
                    label={opt.label}
                    count={opt.count}
                    checkboxValue={encodeFilterCheckboxValue(
                      group.id,
                      opt.value,
                    )}
                  />
                );
              })}
            </Flex>
          </Checkbox.Group>
          <CollapsibleFilterFooter
            show={collapse.showToggle}
            expanded={collapse.expanded}
            onToggle={() => collapse.setExpanded((v) => !v)}
          />
        </>
      )}
    </FilterGroupSection>
  );
}

function CheckboxSingleView({
  option,
  showDivider,
}: {
  option: Extract<FilterGroup, { kind: 'single-checkbox' }>;
  showDivider?: boolean;
}) {
  return (
    <FilterGroupSection showDivider={showDivider}>
      <FilterCheckboxOptionRow
        label={option.label}
        labelSuffix={option.labelSuffix}
        count={option.count}
        checked={option.checked}
        onCheckedChange={option.onChange}
      />
    </FilterGroupSection>
  );
}

function SelectGroupView({
  group,
  showDivider,
}: {
  group: Extract<FilterGroup, { kind: 'select' }>;
  showDivider?: boolean;
}) {
  const hasOptionCounts = group.options.some((opt) => opt.count != null);
  const isMultiple = group.mode === 'multiple';

  return (
    <FilterGroupSection title={group.title} showDivider={showDivider}>
      <Select
        data-testid={`filtered-listing-filter-${group.id}`}
        classNames={{
          popup: { root: `filtered-listing-filter-${group.id}-dropdown` },
        }}
        style={{ width: '100%' }}
        placeholder={group.placeholder}
        allowClear={group.allowClear}
        mode={isMultiple ? 'multiple' : undefined}
        maxTagCount={isMultiple ? 'responsive' : undefined}
        showSearch={group.showSearch ? defaultFilterShowSearch : undefined}
        value={group.value}
        options={group.options}
        optionRender={
          hasOptionCounts
            ? (option) => {
                const count = (option.data as FilterSelectOption | undefined)
                  ?.count;
                return (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={8}
                    style={{ width: '100%' }}
                  >
                    <Flex style={{ minWidth: 0, flex: 1 }}>{option.label}</Flex>
                    {count != null ? (
                      <Meta
                        style={{
                          flexShrink: 0,
                          minWidth: 24,
                          textAlign: 'right',
                        }}
                      >
                        {count}
                      </Meta>
                    ) : null}
                  </Flex>
                );
              }
            : undefined
        }
        onChange={(value) => {
          if (group.mode === 'multiple') {
            group.onChange(Array.isArray(value) ? value.map(String) : []);
            return;
          }
          group.onChange(
            value == null || value === '' ? undefined : String(value),
          );
        }}
      />
    </FilterGroupSection>
  );
}

function TextInputGroupView({
  group,
  showDivider,
}: {
  group: Extract<FilterGroup, { kind: 'text-input' }>;
  showDivider?: boolean;
}) {
  const [localValue, setLocalValue] = useState(group.value);
  const [prevValue, setPrevValue] = useState(group.value);
  const debouncedOnChange = useDebounceCallback(group.onChange, 300);

  if (prevValue !== group.value) {
    setPrevValue(group.value);
    setLocalValue(group.value);
  }

  return (
    <FilterGroupSection title={group.title} showDivider={showDivider}>
      <Input
        allowClear
        placeholder={group.placeholder}
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          debouncedOnChange(e.target.value);
        }}
      />
    </FilterGroupSection>
  );
}

function NumberRangeGroupView({
  group,
  showDivider,
}: {
  group: Extract<FilterGroup, { kind: 'number-range' }>;
  showDivider?: boolean;
}) {
  const [localFrom, setLocalFrom] = useState(group.valueFrom);
  const [localTo, setLocalTo] = useState(group.valueTo);
  const [prevFrom, setPrevFrom] = useState(group.valueFrom);
  const [prevTo, setPrevTo] = useState(group.valueTo);
  const debouncedOnChangeFrom = useDebounceCallback(group.onChangeFrom, 300);
  const debouncedOnChangeTo = useDebounceCallback(group.onChangeTo, 300);

  if (prevFrom !== group.valueFrom) {
    setPrevFrom(group.valueFrom);
    setLocalFrom(group.valueFrom);
  }

  if (prevTo !== group.valueTo) {
    setPrevTo(group.valueTo);
    setLocalTo(group.valueTo);
  }

  return (
    <FilterGroupSection title={group.title} showDivider={showDivider}>
      <Flex gap={4}>
        <Input
          type="number"
          placeholder={group.placeholderFrom}
          value={localFrom}
          onChange={(e) => {
            setLocalFrom(e.target.value);
            debouncedOnChangeFrom(e.target.value);
          }}
        />
        <Input
          type="number"
          placeholder={group.placeholderTo}
          value={localTo}
          onChange={(e) => {
            setLocalTo(e.target.value);
            debouncedOnChangeTo(e.target.value);
          }}
        />
      </Flex>
    </FilterGroupSection>
  );
}

function DateRangeGroupView({
  group,
  showDivider,
}: {
  group: Extract<FilterGroup, { kind: 'date-range' }>;
  showDivider?: boolean;
}) {
  return (
    <FilterGroupSection title={group.title} showDivider={showDivider}>
      <RangePicker
        data-testid={`filtered-listing-filter-${group.id}`}
        placeholder={
          group.placeholderFrom != null || group.placeholderTo != null
            ? [group.placeholderFrom ?? '', group.placeholderTo ?? '']
            : undefined
        }
        allowClear={group.allowClear}
        allowEmpty={group.allowEmpty}
        value={group.value}
        onChange={(dates) => group.onChange(dates ?? null)}
      />
    </FilterGroupSection>
  );
}

function renderFilterGroup(group: FilterGroup, showDivider?: boolean) {
  if (group.kind === 'checkbox-list') {
    return (
      <CheckboxListGroupView
        key={group.id}
        group={group}
        showDivider={showDivider}
      />
    );
  }

  if (group.kind === 'single-checkbox') {
    return (
      <CheckboxSingleView
        key={group.id}
        option={group}
        showDivider={showDivider}
      />
    );
  }

  if (group.kind === 'select') {
    return (
      <SelectGroupView key={group.id} group={group} showDivider={showDivider} />
    );
  }
  if (group.kind === 'text-input') {
    return (
      <TextInputGroupView
        key={group.id}
        group={group}
        showDivider={showDivider}
      />
    );
  }
  if (group.kind === 'number-range') {
    return (
      <NumberRangeGroupView
        key={group.id}
        group={group}
        showDivider={showDivider}
      />
    );
  }
  if (group.kind === 'date-range') {
    return (
      <DateRangeGroupView
        key={group.id}
        group={group}
        showDivider={showDivider}
      />
    );
  }
  if (group.kind === 'custom') {
    return (
      <FilterGroupSection
        key={group.id}
        title={group.title}
        showDivider={showDivider}
      >
        {group.content}
      </FilterGroupSection>
    );
  }
  return <></>; // should never happen
}

function FilteredListingFilterGroups({
  filterGroups,
  showDivider,
}: {
  filterGroups: FilterGroup[];
  showDivider?: boolean;
}) {
  return filterGroups.map((group) => renderFilterGroup(group, showDivider));
}

function FilteredListingClearFiltersButton({
  filtersActive,
  onClearAllFilters,
}: {
  filtersActive: boolean;
  onClearAllFilters?: () => void;
}) {
  const { t } = useTranslation();
  if (!filtersActive || !onClearAllFilters) return null;
  return (
    <Button
      type="link"
      size="small"
      data-testid="filtered-listing-clear-filters"
      onClick={onClearAllFilters}
    >
      {t('CLEAR_FILTERS')}
    </Button>
  );
}

type FilteredListingHeaderFiltersProps = {
  search?: FilteredListingSearchRowProps;
  topFilters?: React.ReactNode;
  filterGroups: FilterGroup[];
  filtersActive: boolean;
  onClearAllFilters?: () => void;
};

export const FilteredListingHeaderFilters = memo(
  function FilteredListingHeaderFilters({
    search,
    topFilters,
    filterGroups,
    filtersActive,
    onClearAllFilters,
  }: FilteredListingHeaderFiltersProps) {
    return (
      <Flex align="center" wrap gap="small">
        {search ? <FilteredListingSearchRow {...search} /> : null}
        {topFilters != null && topFilters !== false ? topFilters : null}
        <FilteredListingFilterGroups
          filterGroups={filterGroups}
          showDivider={false}
        />
        <FilteredListingClearFiltersButton
          filtersActive={filtersActive}
          onClearAllFilters={onClearAllFilters}
        />
      </Flex>
    );
  },
);

type FilteredListingFilterSidebarProps = {
  search?: FilteredListingSearchRowProps;
  topFilters?: React.ReactNode;
  filterGroups: FilterGroup[];
  filtersActive: boolean;
  onClearAllFilters?: () => void;
};

export const FilteredListingFilterSidebar = memo(
  function FilteredListingFilterSidebar({
    search,
    topFilters,
    filterGroups,
    filtersActive,
    onClearAllFilters,
  }: FilteredListingFilterSidebarProps) {
    return (
      <Card data-testid="filtered-listing-filter-sidebar">
        {search ? (
          <SidebarTopToolbarSection>
            <FilteredListingSearchRow {...search} />
          </SidebarTopToolbarSection>
        ) : null}

        {topFilters != null && topFilters !== false && (
          <SidebarTopToolbarSection>{topFilters}</SidebarTopToolbarSection>
        )}

        {filtersActive && onClearAllFilters ? (
          <SidebarSection>
            <FilteredListingClearFiltersButton
              filtersActive={filtersActive}
              onClearAllFilters={onClearAllFilters}
            />
          </SidebarSection>
        ) : null}

        <FilteredListingFilterGroups filterGroups={filterGroups} />
      </Card>
    );
  },
);
